import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useEffect, useReducer } from "react";

export interface SiteSetting {
  id: string;
  setting_key: string;
  setting_value: string;
  setting_label: string;
  setting_type: string;
  min_value: number | null;
  max_value: number | null;
  created_at: string;
  updated_at: string;
}

export function useSiteSettings() {
  const queryClient = useQueryClient();

  const { data: settings = [], isLoading } = useQuery({
    queryKey: ["site-settings"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("site_settings")
        .select("*")
        .order("setting_key");

      if (error) throw error;
      return data as SiteSetting[];
    },
    // Realtime should keep clients in sync, but some mobile browsers/network setups
    // can block/interrupt websockets. This polling is a safe fallback.
    refetchOnMount: "always",
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
    refetchInterval: 15_000,
  });

  // Keep all open clients in sync when an admin updates settings.
  // (Requires the table to be enabled for realtime on the backend.)
  useEffect(() => {
    const channel = supabase
      .channel("site_settings_realtime")
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "site_settings" },
        () => {
          queryClient.invalidateQueries({ queryKey: ["site-settings"] });
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [queryClient]);

  const updateSetting = useMutation({
    mutationFn: async ({ key, value }: { key: string; value: string }) => {
      const { error } = await supabase
        .from("site_settings")
        .update({ setting_value: value })
        .eq("setting_key", key);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["site-settings"] });
    },
  });

  const getSetting = (key: string): string | null => {
    const setting = settings.find((s) => s.setting_key === key);
    return setting?.setting_value ?? null;
  };

  const getSettingDetails = (key: string): SiteSetting | null => {
    return settings.find((s) => s.setting_key === key) ?? null;
  };

  return {
    settings,
    isLoading,
    getSetting,
    getSettingDetails,
    updateSetting,
  };
}

export type CTABrowserPlatform =
  | "ios"
  | "safari"
  | "chrome"
  | "edge"
  | "firefox"
  | "default";

const CTA_PREVIEW_OVERRIDE_ENABLED_KEY = "anaya_cta_preview_enabled";
const CTA_PREVIEW_OVERRIDE_PLATFORM_KEY = "anaya_cta_preview_platform";
const CTA_PREVIEW_EVENT = "anaya:cta-preview-override-change";

function normalizePlatform(value: unknown): CTABrowserPlatform | null {
  if (typeof value !== "string") return null;
  const v = value.toLowerCase().trim();
  if (
    v === "ios" ||
    v === "safari" ||
    v === "chrome" ||
    v === "edge" ||
    v === "firefox" ||
    v === "default"
  ) {
    return v;
  }
  return null;
}

export function getCTAPreviewOverride(): {
  enabled: boolean;
  platform: CTABrowserPlatform;
} {
  try {
    const enabled = localStorage.getItem(CTA_PREVIEW_OVERRIDE_ENABLED_KEY) === "1";
    const platform =
      normalizePlatform(localStorage.getItem(CTA_PREVIEW_OVERRIDE_PLATFORM_KEY)) ??
      "default";
    return { enabled, platform };
  } catch {
    return { enabled: false, platform: "default" };
  }
}

export function setCTAPreviewOverride(next: {
  enabled: boolean;
  platform: CTABrowserPlatform;
}) {
  try {
    localStorage.setItem(CTA_PREVIEW_OVERRIDE_ENABLED_KEY, next.enabled ? "1" : "0");
    localStorage.setItem(CTA_PREVIEW_OVERRIDE_PLATFORM_KEY, next.platform);
  } catch {
    // ignore (private mode / storage blocked)
  }
  window.dispatchEvent(new Event(CTA_PREVIEW_EVENT));
}

export function clearCTAPreviewOverride() {
  try {
    localStorage.removeItem(CTA_PREVIEW_OVERRIDE_ENABLED_KEY);
    localStorage.removeItem(CTA_PREVIEW_OVERRIDE_PLATFORM_KEY);
  } catch {
    // ignore
  }
  window.dispatchEvent(new Event(CTA_PREVIEW_EVENT));
}

// Detect browser/platform
function detectPlatform(): CTABrowserPlatform {
  const ua = navigator.userAgent.toLowerCase();

  // Device flag is used elsewhere (CSS safe-area handling). For CTA offsets we want
  // browser-level detection even on iOS (Chrome/Edge/Firefox in-app browsers).
  const isIOSDevice =
    /iphone|ipad|ipod/.test(ua) ||
    (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);

  // iOS UA tokens we want to recognize:
  // - Chrome: CriOS
  // - Edge: EdgiOS
  // - Firefox: FxiOS
  const isEdge = /edg|edge/.test(ua);
  const isChrome = /chrome|crios/.test(ua) && !isEdge;
  const isFirefox = /firefox|fxios/.test(ua);
  const isSafari =
    /safari/.test(ua) &&
    !isChrome &&
    !isEdge &&
    !isFirefox &&
    !/android/.test(ua);

  if (isEdge) return "edge";
  if (isChrome) return "chrome";
  if (isFirefox) return "firefox";

  // iOS Safari uses the dedicated "ios" setting (adds safe-area via CSS).
  if (isSafari && isIOSDevice) return "ios";

  // macOS Safari (and other Safari-like environments)
  if (isSafari) return "safari";

  return "default";
}

export function detectCTABrowserPlatform(): CTABrowserPlatform {
  return detectPlatform();
}

// Hook to apply CTA position CSS variable based on detected platform
export function useApplyCTAPosition() {
  const { settings, isLoading } = useSiteSettings();
  const [overrideVersion, bumpOverrideVersion] = useReducer((x) => x + 1, 0);

  useEffect(() => {
    const onOverrideChange = () => bumpOverrideVersion();
    window.addEventListener(CTA_PREVIEW_EVENT, onOverrideChange);
    // also respond to localStorage changes from other tabs
    window.addEventListener("storage", onOverrideChange);

    return () => {
      window.removeEventListener(CTA_PREVIEW_EVENT, onOverrideChange);
      window.removeEventListener("storage", onOverrideChange);
    };
  }, []);

  useEffect(() => {
    if (isLoading || settings.length === 0) return;

    const preview = getCTAPreviewOverride();
    const platform = preview.enabled ? preview.platform : detectPlatform();
    const settingKey = `${platform}_cta_bottom_extra`;

    // Helper to get setting value from settings array
    const getSettingValue = (key: string): string | null => {
      const setting = settings.find((s) => s.setting_key === key);
      return setting?.setting_value ?? null;
    };

    // Try platform-specific setting first, fall back to default
    let value = getSettingValue(settingKey);
    if (!value) {
      value = getSettingValue("default_cta_bottom_extra");
    }
    // Final fallback to iOS setting for backward compatibility
    if (!value) {
      value = getSettingValue("ios_cta_bottom_extra");
    }

    if (value) {
      const parsed = Number.parseInt(value, 10);
      const px = Number.isFinite(parsed) ? parsed : 40;

      document.documentElement.style.setProperty("--hero-cta-bottom-extra", `${px}px`);
    }
  }, [settings, isLoading, overrideVersion]);
}

// Legacy hook for backward compatibility
export function useApplyIOSCTAPosition() {
  useApplyCTAPosition();
}

